import React, { useMemo, useState } from 'react';
import { gql, useQuery } from '@apollo/client';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import { DATE_TIME_FORMAT_MONTH_DAY_HOUR_MINUTE } from '../../utils/dates';
import FeedFloTextInput from '../../atoms/FeedFloTextInput/FeedFloTextInput';

import Checkbox from '../../atoms/Checkbox/Checkbox';
import { getFaultName } from '@feedflo/fault-codes';
import MessageHistoryLink from '../../molecules/MessageHistoryLink';
import './DeviceFaultTable.scss';

const DEVICE_GQL = gql`
  query DeviceFaultQuery($deviceId: uuid!, $start: bigint!, $end: bigint!) {
    faultCodes: fault(
      where: {
        device_id: { _eq: $deviceId }
        started_at: { _lte: $end }
        _or: [{ ended_at: { _gte: $start } }, { ended_at: { _is_null: true }, started_at: { _gte: $start } }]
        code: { _nin: [1005] }
      }
      order_by: { started_at: desc }
    ) {
      started_at
      ended_at
      code
      id
      details
    }
  }
`;

const FEED_FRAME_GQL = gql`
  query FeedFrameQuery($deviceId: uuid!, $start: bigint!, $end: bigint!) {
    feed_frame(where: { device_id: { _eq: $deviceId }, started_at: { _lte: $end }, ended_at: { _gte: $start } }) {
      id
      started_at
      ended_at
    }
  }
`;

export default function DeviceFaultTable({ deviceId, barnId = null, start = null, end = null }) {
  const now = useMemo(() => {
    return Math.floor(Date.now() / 1000);
  }, []);

  start = start || now - 4 * 60 * 60;
  end = end || now;

  const { loading, error, data } = useQuery(DEVICE_GQL, {
    variables: {
      deviceId,
      start,
      end,
    },
  });

  const { data: feedFrameData } = useQuery(FEED_FRAME_GQL, {
    variables: {
      deviceId,
      start,
      end,
    },
  });

  const safeGetFaultName = (code) => {
    try {
      return getFaultName(code);
    } catch (error) {
      return code;
    }
  };

  // Create a lookup map for feed frames by started_at timestamp
  const feedFramesByStartTime = useMemo(() => {
    const map = new Map();
    feedFrameData?.feed_frame?.forEach((frame) => {
      map.set(frame.started_at, frame);
    });
    return map;
  }, [feedFrameData]);

  const faultCodes = data?.faultCodes;
  const [faultCodeSearchText, setFaultCodeSearchText] = useState('');
  const [hideFeedFrames, setHideFeedFrames] = useState(false);
  const filteredFaultCodes = useMemo(() => {
    let filtered = faultCodes;

    // Filter by search text
    if (faultCodeSearchText !== '') {
      const codeTokens = faultCodeSearchText.split(',').filter((token) => token.length);
      filtered = filtered?.filter((fault) => codeTokens.some((token) => fault.code.toString().startsWith(token)));
    }

    // Filter out 2002 faults if hideFeedFrames is checked
    if (hideFeedFrames) {
      filtered = filtered?.filter((fault) => fault.code !== 2002);
    }

    return filtered;
  }, [faultCodes, faultCodeSearchText, hideFeedFrames]);

  if (loading) return <span>Loading</span>;
  if (error) return <span>{JSON.stringify(error, null, 2)}</span>;

  const faultView = (
    <div className="faultCodeHolder">
      <div className="faultLine">
        <table>
          <thead>
            <tr>
              <th>Code</th>
              <th>Started</th>
              <th>Ended</th>
              <th>Duration</th>
              <th>Message</th>
              <th>Feed Frame</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {filteredFaultCodes?.map((fCode) => {
              // Convert date values into seconds
              const from = fCode.started_at;
              const to = fCode.ended_at || from + 24 * 60 * 60; // Use the end date if the fault has ended, otherwise use 24 hours

              let icon = '🔷';
              if (1000 <= fCode.code && fCode.code <= 1999) {
                icon = '✅';
              } else if (fCode.code === 2001 || fCode.code === 2002) {
                icon = fCode.ended_at ? '🏁' : '🏃';
              } else if (3000 <= fCode.code && fCode.code <= 3999) {
                icon = '❌';
              }
              // Check if this is a 2002 fault and if there's a matching feed frame
              const matchingFeedFrame = fCode.code === 2002 ? feedFramesByStartTime.get(fCode.started_at) : null;

              return (
                <tr key={fCode.id}>
                  <td title={fCode.code}>{`${icon} ${safeGetFaultName(fCode.code)} (${fCode.code})`}</td>
                  <td>{dayjs.tz(1000 * from).format(DATE_TIME_FORMAT_MONTH_DAY_HOUR_MINUTE)}</td>
                  <td>
                    {fCode.ended_at ? dayjs.tz(1000 * to).format(DATE_TIME_FORMAT_MONTH_DAY_HOUR_MINUTE) : 'None'}
                  </td>
                  <td>{dayjs.tz(1000 * to || Date.now()).from(from * 1000, true)}</td>
                  <td>
                    {fCode.details ? (
                      <span title={atob(fCode.details)}>
                        {atob(fCode.details).length > 256
                          ? `${atob(fCode.details).substring(0, 256)}...`
                          : atob(fCode.details)}
                      </span>
                    ) : (
                      <i>&lt;No Message&gt;</i>
                    )}
                  </td>
                  <td>
                    {matchingFeedFrame && (
                      <Link target="_blank" to={`/ff/${matchingFeedFrame.id}`}>
                        view analysis
                      </Link>
                    )}
                  </td>
                  <td>
                    <Link target="_blank" to={`/rawData/${deviceId}?to=${to}&from=${from}`}>
                      📈
                    </Link>
                    <Link target="_blank" to={`/fault/${fCode.id}`}>
                      🔎
                    </Link>
                    <MessageHistoryLink
                      filters={{
                        barnId: barnId || undefined,
                        type: fCode.code,
                        from,
                        to,
                      }}
                      variant="compact"
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );

  return (
    <div className="DeviceFaultTable">
      <div className="DeviceFaultTable-controls">
        <FeedFloTextInput
          defaultText="Search by Fault Code"
          onChange={(value) => setFaultCodeSearchText(value.trim())}
        />
        <Checkbox
          label="Hide Feed Frames"
          checked={hideFeedFrames}
          onChange={(e) => setHideFeedFrames(e.target.checked)}
        />
      </div>
      {faultView}
    </div>
  );
}

DeviceFaultTable.propTypes = {
  deviceId: PropTypes.string,
  barnId: PropTypes.string,
  start: PropTypes.number,
  end: PropTypes.number,
};
